if (!customElements.get('secure-your-cart')) {
  customElements.define('secure-your-cart', class SecureYourCart extends HTMLElement {
    constructor() {
      super();
      this.initialised = false;
    }

    connectedCallback() {
      if (this.initialised) {
        return;
      }

      this.init();

      this.initialised = true;
    }

    disconnectedCallback() {
      this.cleanupDetachedModal();
      document.removeEventListener('modal:fully-closed', this.modalCloseListener);
      document.removeEventListener('cart:updated', this.cartUpdatedListener);
    }

    /**
     * @method init
     * @description Initialises SYC by setting up necessary elements, states and events
     * @returns {void}
     */
    init() {
      this.debugLog('Component mounted');

      this.initGlobalVariables();
      this.enableCheck();

      if (!this.initElements()) {
        return;
      }

      this.initModalStates();
      this.initCloseCTA();
      this.initEvents();
    }

    /**
     * @method initGlobalVariables
     * @description Initialises global variables based on data attributes
     * @returns {void}
     */
    initGlobalVariables() {
      this.containerEnabledClass = 'secure-your-cart__container--enabled';
      this.savingsPlaceholder = '[SAVINGS]';

      this.blockId = this.dataset.blockId;
      this.initialEligibility = this.dataset.initialEligibility === 'true';
      this.forceEnable = this.dataset.forceEnable === 'true';
      this.apiKey = this.dataset.apiKey;
      this.optInId = this.dataset.listIdOptIn;
      this.optOutId = this.dataset.listIdOptOut;
      this.permalinkBehavior = this.dataset.permalinkBehavior || 'cart';
      this.customerId = this.dataset.customerId || null;

      this.hideAfterSubmit = this.dataset.hasOwnProperty('hideAfterSubmit');
      this.reEnableDelay = parseInt(this.dataset.reEnableDelay, 10) || 24;

      this.totalSavings = (parseFloat(this.dataset.totalSavings) / 100) || 0;
    }

    /**
     * @method enableContainer
     * @description Adds the enabled class to the container to show the SYC CTA
     * @returns {void}
     */
    enableContainer() {
      this.parentElement.classList.add(this.containerEnabledClass);
    }

    /**
     * @method disableContainer
     * @description Removes the enabled class from the container to hide the SYC CTA
     * @returns {void}
     */
    disableContainer() {
      this.parentElement.classList.remove(this.containerEnabledClass);
    }

    /**
     * @method enableCheck
     * @description Determines whether the SYC should be enabled based on settings and cookies
     * @returns {void}
     */
    enableCheck() {
      this.enablementBlocked = false;

      // Handle missing Klaviyo config
      if (!this.apiKey || !this.optInId || !this.optOutId) {
        this.debugLog('Missing Klaviyo configuration, hiding CTA');
        this.enablementBlocked = true;
        this.disableContainer();
        return;
      }

      // Handle hide after submit functionality based on cookie presence
      if (this.hideAfterSubmit && this.checkHideCookie()) {
        this.debugLog('Hide after submit enabled and hide cookie present, hiding CTA');
        this.enablementBlocked = true;
        this.disableContainer();
        return;
      }

      this.refreshEnablement();
    }

    /**
     * @method isCartEligible
     * @description Determines whether the provided cart meets the SYC product-mix criteria
     * @param {object|null} cart - Cart object used for determining enablement
     * @returns {boolean}
     */
    isCartEligible(cart) {
      if (!Array.isArray(cart?.items) || cart.items.length === 0) {
        return false;
      }

      const cartCheck = cart.items.reduce((acc, item) => {
        if (item.product_type === 'Mattress' || item.product_type === 'Bed Frame') {
          acc.containsMattressOrBedframe = true;
        } else {
          acc.containsOtherProducts = true;
        }

        return acc;
      }, {
        containsMattressOrBedframe: false,
        containsOtherProducts: false,
      });

      return cartCheck.containsMattressOrBedframe && cartCheck.containsOtherProducts;
    }

    /**
     * @method initElements
     * @description Finds and initialises key SYC elements
     * @returns {void}
     */
    initElements() {
      if (!this.initCartCTA()) {
        this.teardown();
        return false;
      }

      if (!this.initModal()) {
        this.teardown();
        return false;
      }

      this.initStateSwitchCTAs();
      this.replaceSavingsPlaceholders();
      this.initFormElements();
      this.initCloseCTA();

      return true;
    }

    /**
     * @method initStateSwitchCTAs
     * @description Finds any state switch CTAs and adds listeners to switch modal states
     * @returns {void}
     */
    initStateSwitchCTAs() {
      const stateSwitchEls = this.modal.querySelectorAll('[data-switch-state]');

      stateSwitchEls.forEach(button => {
        button.addEventListener('click', () => {
          const targetState = button.dataset.switchState;
          const skipMethods = button.dataset.skipMethods === 'true';
          this.setActiveModalState(targetState, skipMethods);
        });
      });
    }

    /**
     * @method replaceSavingsPlaceholders
     * @description Finds all instances of savings placeholders and replaces them with the total savings value, wrapped in a span for styling
     * @param {string|null} [override=null] - An optional override value to use instead of the total savings for replacement, used to reflect savings after the cart has been updated without a page refresh
     * @returns {void}
     */
    replaceSavingsPlaceholders(override = null) {
      const targetElements = this.getSavingsTextElements();
      const savingsValue = override === null ? this.totalSavings : this.normaliseSavingsValue(override);

      targetElements.forEach(el => {
        if (el.textContent.includes(this.savingsPlaceholder)) {
          el.innerHTML = el.textContent.replace(
            this.savingsPlaceholder,
            `<span class="modal--syc__savings">
              ${
                savingsValue.toLocaleString('en-GB', {
                  style: 'currency',
                  currency: 'GBP',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })
              } savings
            </span>`
          );
        }
      });
    }

    /**
     * @method updateRenderedSavingsValues
     * @description Updates any already-rendered savings span values without requiring placeholders to be present
     * @param {string|number|null} [override=null] - An optional override value in minor units
     * @returns {void}
     */
    updateRenderedSavingsValues(override = null) {
      const savingsValue = override === null ? this.totalSavings : this.normaliseSavingsValue(override);

      this.getRenderedSavingsElements().forEach(el => {
        el.textContent = `${this.formatSavingsValue(savingsValue)} savings`;
      });
    }

    /**
     * @method refreshEnablement
     * @description Refreshes whether the SYC should be shown
     * @param {object|null} cart - Cart object used for determining enablement
     */
    refreshEnablement(cart) {
      if (this.enablementBlocked) {
        this.disableContainer();
        return;
      }

      if (Array.isArray(cart?.items) && cart.items.length === 0) {
        this.disableContainer();
        return;
      }

      // If force enable is on, skip all checks and just enable the container
      if (this.forceEnable) {
        this.enableContainer();
        return;
      }

      if (typeof cart === 'undefined' || cart === null) {
        if (this.initialEligibility) {
          this.enableContainer();
          return;
        }

        this.disableContainer();
        return;
      }

      if (this.isCartEligible(cart)) {
        this.enableContainer();
        return;
      }

      // Hide SYC if none of the above conditions are met
      this.disableContainer();
    }

    /**
     * @method refreshSavingsDisplay
     * @description Updates the component's savings value and refreshes both placeholder-driven and rendered savings markup
     * @param {string|number|null} [override=null] - An optional override value in minor units
     * @returns {void}
     */
    refreshSavingsDisplay(override = null) {
      const nextSavings = override === null ? this.getCartSavingsTotal() : override;

      if (nextSavings === null || nextSavings === undefined || nextSavings === '') {
        this.debugLog('No updated savings value available from cart');
        return;
      }

      this.totalSavings = this.normaliseSavingsValue(nextSavings);
      this.dataset.totalSavings = String(nextSavings);

      this.replaceSavingsPlaceholders(nextSavings);
      this.updateRenderedSavingsValues(nextSavings);

      this.debugLog(`Savings display updated to ${this.formatSavingsValue(this.totalSavings)}`);
    }

    /**
     * @method getSavingsTextElements
     * @description Returns text elements within the component and detached modal that may contain savings placeholders
     * @returns {HTMLElement[]}
     */
    getSavingsTextElements() {
      const elements = new Set();

      this.querySelectorAll('p, h3').forEach(el => elements.add(el));
      this.modal?.querySelectorAll('p, h3').forEach(el => elements.add(el));

      return Array.from(elements);
    }

    /**
     * @method getRenderedSavingsElements
     * @description Returns all rendered savings span elements in the component and detached modal
     * @returns {HTMLElement[]}
     */
    getRenderedSavingsElements() {
      const elements = new Set();

      this.querySelectorAll('.modal--syc__savings').forEach(el => elements.add(el));
      this.modal?.querySelectorAll('.modal--syc__savings').forEach(el => elements.add(el));

      return Array.from(elements);
    }

    /**
     * @method getCartSavingsTotal
     * @description Reads the current total savings from the live cart drawer DOM
     * @returns {string|null}
     */
    getCartSavingsTotal() {
      return document.querySelector('[data-total-value-saving-total]')?.dataset.totalValueSavingTotal ?? null;
    }

    /**
     * @method normaliseSavingsValue
     * @description Converts a savings value in minor units to a numeric major-unit amount
     * @param {string|number|null} value - The value to normalise
     * @returns {number}
     */
    normaliseSavingsValue(value) {
      const parsedValue = Number.parseFloat(value);

      if (Number.isNaN(parsedValue)) {
        return 0;
      }

      return parsedValue / 100;
    }

    /**
     * @method formatSavingsValue
     * @description Formats a numeric savings value as GBP currency
     * @param {number} value - The value to format
     * @returns {string}
     */
    formatSavingsValue(value) {
      return value.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    /**
     * @method initFormElements
     * @description Finds and stores references to form elements within the modal for later use
     * @returns {void}
     */
    initFormElements() {
      this.emailEl = this.modal.querySelector('#syc_email');
      this.emailErrorEl = this.modal.querySelector('.modal--syc__email__error');
      this.optInEl = this.modal.querySelector('#syc_opt_in');

      this.assignUniqueFormControlIds();
    }

    assignUniqueFormControlIds() {
      if (!this.modal) {
        return;
      }

      const modalKey = this.blockId || 'default';
      const emailId = `syc_email_${modalKey}`;
      const optInId = `syc_opt_in_${modalKey}`;
      const optInLabel = this.modal.querySelector('label[for="syc_opt_in"]');

      if (this.emailEl) {
        this.emailEl.id = emailId;
      }

      if (this.optInEl) {
        this.optInEl.id = optInId;
      }

      if (optInLabel) {
        optInLabel.setAttribute('for', optInId);
      }
    }

    /**
     * @method initModal
     * @description Finds modal-related elements and calls any required setup methods
     * @returns {void}
     */
    initModal() {
      this.modal = this.querySelector('[data-modal-syc]');
      this.underlay = document.querySelector('.underlay');

      if (this.modal && this.underlay) {
        this.debugLog('Modal and underlay found');
        this.modalCloseCTA = this.modal.querySelector('.modal__close');
        this.moveModalToBody();

        // Listen for the SYC modal being closed to reset to initial state
        this.modalCloseListener = e => {
          if (e.detail.modalIdentifier === 'syc') {
            this.setActiveModalState('initial');
          }
        };

        this.cartUpdatedListener = e => {
          const nextCart = e.detail?.cart ?? null;

          this.initialEligibility = this.forceEnable || this.isCartEligible(nextCart);
          this.refreshEnablement(nextCart);
          this.refreshSavingsDisplay(e.detail?.totalSavings ?? null);
        };

        document.addEventListener('modal:fully-closed', this.modalCloseListener);
        document.addEventListener('cart:updated', this.cartUpdatedListener);

        return true;
      } else {
        this.debugLog('Modal and/or underlay not found');
        return false;
      }
    }

    /**
     * @method initCartCTA
     * @description Finds the cart CTA element and calls any required setup methods
     * @returns {void}
     */
    initCartCTA() {
      this.cartCTA = this.querySelector('[data-element="cart-cta"]');

      if (this.cartCTA) {
        this.debugLog('Cart CTA found');
        return true;
      } else {
        this.debugLog('No Cart CTA found');
        return false;
      }
    }

    initCloseCTA() {
      this.modalSuccessCloseCTA = this.modal.querySelector('[data-action="close"]');

      if (this.modalSuccessCloseCTA) {
        this.debugLog('Modal close CTA found');
      }
    }

    /**
     * @method teardown
     * @description Clears component markup and removes detached modal/host to stop further use
     * @returns {void}
     */
    teardown() {
      this.cleanupDetachedModal();

      // Tidy up event listener for modal close
      document.removeEventListener('modal:fully-closed', this.modalCloseListener);
      document.removeEventListener('cart:updated', this.cartUpdatedListener);

      // Clear host element content and remove from DOM
      this.replaceChildren();
      this.remove();
      this.debugLog('Component torn down');
    }

    /**
     * @method initModalStates
     * @description Initialises the modal states and sets up internal active status tracking for each
     * @returns {void}
     */
    initModalStates() {
      const availableModalStates = Array.from(this.modal.querySelectorAll('[data-state]'));

      this.modalStates = availableModalStates.reduce((states, element) => {
        const stateKey = element.dataset.state;
        states[stateKey] = {
          element,
          active: element.dataset.active === 'true',
        };

        return states;
      }, {});

      if (Object.keys(this.modalStates).length > 0) {
        Object.entries(this.modalStates).forEach(([key, state]) => {
          if (state.element) {
            this.debugLog(`Modal state element found for key: ${key}`);
          } else {
            this.debugLog(`No modal state element found for key: ${key}`);
          }
        });
      } else {
        this.debugLog('No modal states found');
      }
    }

    /**
     * @method initEvents
     * @description Sets up event listeners for SYC interactions
     * @returns {void}
     */
    initEvents() {
      // Handle cart CTA click to open SYC modal
      this.cartCTA.addEventListener('click', () => {
        this.reset();
        this.refreshSavingsDisplay();
        this.modal.classList.add('modal--open');
        this.underlay.classList.add('underlay--open', 'underlay--above-side-cart');
      });

      // Handle changes to the email address
      this.emailEl.addEventListener('input', () => {
        if (this.emailEl.validity.valid) {
          this.emailEl.classList.remove('modal--syc__email--error');
          this.emailErrorEl.classList.remove('modal--syc__email__error--visible');
          this.modal.querySelector('.modal--syc__submit').removeAttribute('disabled');
        } else {
          this.emailEl.classList.add('modal--syc__email--error');
          this.emailErrorEl.classList.add('modal--syc__email__error--visible');
          this.modal.querySelector('.modal--syc__submit').setAttribute('disabled', 'true');
        }
      });

      if (this.modalCloseCTA) {
        this.modalCloseCTA.addEventListener('click', () => {
          this.closeModal();
        });
      }

      this.modalSuccessCloseCTA.addEventListener('click', () => {
        this.closeModal();
      });
    }

    closeModal() {
      this.modal.classList.remove('modal--open');
      this.underlay.classList.remove('underlay--open', 'underlay--above-side-cart');
    }

    /**
     * @method setActiveModalState
     * @description Sets the active modal state based on the provided state key, ensuring only one state is active at a time
     * @param {string} state - The key of the state to set as active
     * @param {boolean} [skipMethods=false] - Whether to skip running any associated activation methods for the state
     * @returns {void}
     * @example setActiveModalState('success');
     */
    setActiveModalState(state, skipMethods = false) {
      if (!this.modalStates?.[state]) {
        this.debugLog(`State '${state}' does not exist`);
        return;
      }

      if (this.modalStates[state].active) {
        this.debugLog(`State '${state}' is already active`);
        return;
      }

      Object.entries(this.modalStates).forEach(([key, value]) => {
        const isIntendedActiveState = key === state;

        this.modalStates[key].active = isIntendedActiveState;
        value.element.dataset.active = String(isIntendedActiveState);

        // If a method exists to run when a state is activated, run it
        if (!skipMethods && isIntendedActiveState) {
          const activationMethodName = `on${key.charAt(0).toUpperCase() + key.slice(1)}StateActivate`;

          if (typeof this[activationMethodName] === 'function') {
            this[activationMethodName]();
          }
        }

        if (isIntendedActiveState && state === 'loading') {
          this.modalCloseCTA.setAttribute('style', 'display: none !important;');
        } else {
          this.modalCloseCTA.removeAttribute('style');
        }
      });
    }

    /**
     * @method moveModalToBody
     * @description Moves the modal element to <body>
     * @returns {void}
     */
    moveModalToBody() {
      document.querySelectorAll('.modal.modal--syc').forEach(existingModal => {
        if (existingModal !== this.modal) {
          existingModal.remove();
        }
      });

      this.modal.dataset.sycOwner = this.blockId || 'default';
      document.body.appendChild(this.modal);
      this.debugLog('Modal moved to <body>');
    }

    cleanupDetachedModal() {
      if (this.modal?.parentNode) {
        this.modal.remove();
      }
    }

    /**
     * @method onLoadingStateActivate
     * @description Handles any functionality required when the loading state is activated
     * @returns {void}
     */
    async onLoadingStateActivate() {
      try {
        // Get the time of form submission
        const submissionTime = new Date();

        // Get current cart data
        const cartData = await this.getCurrentCartData();

        // Generate cart permalink
        const cartPermalink = this.generateCartPermalink(cartData);

        // Handle invalid permalink (e.g. no items in cart)
        if (!cartPermalink) {
          this.debugLog('No cart permalink could be generated, aborting submission');
          throw new Error('Invalid cart permalink');
        }

        // Generate the Klaviyo payload with the submission time and cart permalink
        const klaviyoPayload = this.generateKlaviyoPayload(submissionTime, cartPermalink, cartData);

        // this.setActiveModalState('error', true);

        // Send to Klaviyo
        await this.submitToKlaviyo(klaviyoPayload);

        // Move to the success state
        this.setActiveModalState('success');
      } catch (error) {
        this.debugLog(`Error activating loading state: ${error.message}`);
        this.setActiveModalState('error');
      }
    }

    /**
     * @method onSuccessStateActivate
     * @description Handles any functionality required when the success state is activated
     * @returns {void}
     */
    onSuccessStateActivate() {
      if (this.hideAfterSubmit) {
        this.setHideCookie();
        this.disableContainer();
      }
    }

    /**
     * @method checkHideCookie
     * @description Checks for the presence of the hide cookie for this block and returns a boolean indicating whether the CTA should be hidden
     * @returns {boolean} - Whether the hide cookie is present
     */
    checkHideCookie() {
      const cookieName = `radiant:hide_syc_${this.blockId}`;
      const cookies = document.cookie.split(';').map(cookie => cookie.trim());
      const hideCookie = cookies.find(cookie => cookie.startsWith(`${cookieName}=`));

      const isHidden = Boolean(hideCookie);
      this.debugLog(`Checked for hide cookie with name: ${cookieName}. Present: ${isHidden}`);

      return isHidden;
    }

    /**
     * @method setHideCookie
     * @description Sets a cookie to hide the SYC CTA based on block ID, with an expiry based on the re-enable delay
     * @returns {void}
     */
    setHideCookie() {
      const cookieName = `radiant:hide_syc_${this.blockId}`;
      document.cookie = `${cookieName}=true; max-age=${this.reEnableDelay * 3600}; path=/`;
      this.debugLog(`Set hide cookie with name: ${cookieName}`);
    }

    /**
     * @method getCurrentCartData
     * @description Retrieves current cart data to be used later for generating a cart permalink
     * @returns {object|Promise<Object>} - An object representing the current cart data
     */
    async getCurrentCartData() {
      try {
        const response = await fetch('/cart.js');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
          this.debugLog(`Failed to fetch cart data. Status: ${response.status}`);
        }

        return response.json();

      // If the fetch fails
      } catch (error) {
        this.debugLog(`Error fetching cart data: ${error.message}`);
        throw new Error('Unable to retrieve cart data');
      }
    }

    /**
     * @method generateCartPermalink
     * @description Generates a permalink to the current cart state
     * @param {object} cartData - The current cart data object
     * @returns {string} - A URL representing the permalink to the current cart state
     */
    generateCartPermalink(cartData) {
      // Start with the base URL for the SYC landing page
      let permalink = `${window.location.origin}${window.Shopify.routes.root}?items=`;

      let hasValidItems = false;

      // Loop line items and add appropriate data to the URL
      // (format: variant_id:quantity:property1=value1:property2=value2)
      for (const item of cartData.items) {
        permalink += `${item.id}:${item.quantity}`;

        if (item.properties && Object.keys(item.properties).length > 0) {
          const propertiesString = Object.entries(item.properties)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join(':');
          permalink += `:${propertiesString}`;
        }

        permalink += ',';

        hasValidItems = true;
      }

      // Don't do anything if there are no valid items to include in the permalink (e.g. cart is empty)
      if (!hasValidItems) {
        this.debugLog('No valid items found in cart data to generate permalink');
        return null;
      }

      // Remove trailing comma
      permalink = permalink.slice(0, -1);

      // Add discounts to the URL
      if (Array.isArray(cartData.discount_codes) && cartData.discount_codes.length > 0) {
        const discountsString = cartData.discount_codes
          .filter(({ applicable }) => applicable)
          .map(({ code }) => code)
          .join(',');
        permalink += `&discounts=${discountsString}`;
      }

      // Redirect to checkout rather than cart if configured
      if (this.permalinkBehavior === 'checkout') {
        permalink += '&checkout=true';
      }

      // Add an syc parameter to identify traffic coming from SYC
      permalink += '&syc=true';

      return permalink;
    }

    /**
     * @method generateKlaviyoPayload
     * @description Generates the payload to be sent to Klaviyo based on the current form state and cart permalink
     * @param {Date} submissionTime - The time of form submission to be included in the payload
     * @param {string} cartPermalink - The permalink URL representing the current cart state to be included in the payload
     * @param {object} cartData - The current cart data object
     * @returns {string} - A JSON string representing the payload to be sent to Klaviyo
     */
    generateKlaviyoPayload(submissionTime = new Date(), cartPermalink, cartData) {
      const cartItems = cartData.items.map(item => ({
        productId: item.product_id,
        variantId: item.variant_id,
        title: item.title,
        handle: item.handle,
        quantity: item.quantity,
        size: item.options_with_values.find(({ name }) => name.toUpperCase() === 'SIZE')?.value ?? null,
        price: `£${(item.final_price / 100).toFixed(2)}`,
        linePrice: `£${(item.final_line_price / 100).toFixed(2)}`,
        imageUrl: item?.featured_image?.url ?? null,
      }));

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'secure_your_cart',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: this.emailEl.value,
                  ...(this.customerId && {
                    external_id: this.customerId,
                  }),
                  properties: {
                    cart_permalink: cartPermalink,
                    submission_time: submissionTime.valueOf(),
                    cart_data: {
                      totalSavings: this.formatSavingsValue(this.totalSavings),
                      cartItems,
                    },
                  },
                },
                ...(this.optInEl.checked && {
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED',
                        // consented_at: submissionTime.valueOf(),
                      },
                    },
                  },
                }),
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.optInEl.checked ? this.optInId : this.optOutId,
              },
            },
          },
        },
      };

      return JSON.stringify(payload);
    }

    /**
     * @method submitToKlaviyo
     * @description Submits the generated payload to Klaviyo
     * @param {string} payload - The JSON string representing the payload to be sent to Klaviyo
     * @returns {Promise<void>}
     */
    async submitToKlaviyo(payload) {
      try {
        const klaviyoUrl = `https://a.klaviyo.com/client/subscriptions?company_id=${this.apiKey}`;

        await fetch(klaviyoUrl, {
          method: 'POST',
          headers: {
            revision: '2026-04-15',
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json'
          },
          body: payload,
        });
      } catch (error) {
        this.debugLog(`Error submitting to Klaviyo: ${error.message}`);
        throw new Error('Failed to submit subscription data to Klaviyo');
      }
    }

    /**
     * @method reset
     * @description Resets the modal to its initial state, ready for a new interaction
     * @returns {void}
     */
    reset() {
      // Reset active modal state to initial
      this.setActiveModalState('initial', true);

      // Clear form state
      this.emailEl.value = '';
      this.optInEl.checked = false;
      this.emailErrorEl.classList.remove('modal--syc__email__error--visible');
      this.emailEl.classList.remove('modal--syc__email--error');
      this.modal.querySelector('.modal--syc__submit').setAttribute('disabled', 'true');

      this.debugLog('Modal reset to initial state');
    }

    /**
     * @method debugLog
     * @description Logs debug messages to the console if dev mode is active or if forced
     * @param {string} message - The message to log
     * @param {boolean} [force=false] - Whether to force logging regardless of dev mode status
     * @returns {void}
     * @example debugLog('This is a debug message');
     */
    debugLog(message, force = false) {
      if (document.querySelector('[data-dev-mode]') || force) {
        console.log(
          `%c[SECURE YOUR CART] - %c${message}`,
          'color: #085247; font-weight: bold;',
          'color: #000; font-weight: normal;'
        );
      }
    }
  });
}
